import AbstractShape from "./AbstractShape";
import Coordinate from "../support/Coordinate";
import PolyCoordinate from "../support/PolyCoordinate";
import PolyCornerCoordinate from "../support/PolyCornerCoordinate";
import PolyPotentialCoordinate from "../support/PolyPotentialCoordinate";
import PolyVerticalBorder from "../support/PolyVerticalBorder";
import PolyHorizontalBorder from "../support/PolyHorizontalBorder";
import { createSegment, createStraightSegment, createCurveSegment, createBezierSegment } from "../support/segments/SegmentFactory";
import CurveSegment from "../support/segments/CurveSegment";
import BezierSegment from "../support/segments/BezierSegment";
import EventTypes from "../../shared/EventTypes";

export default class AbstractPolyShape extends AbstractShape {
    center;
    corners;
    hoveredSegment = null;
    selectedSegment = null;

    constructor(provider, data, appearance, speciesIds, label) {
        super(provider, data, appearance, speciesIds, label);

        this.segments = this.buildSegments(data.coordinates);
        this.createSupportCoordinates();
        this.calculateRatio();

        this.on(EventTypes.UPDATE_ELEMENT_SHAPE, (element) => {
            if (element == this) {
                this.calculateRatio();
            }
        })
    }

    calculateRatio() {
        this.coordinates.map((c) => c.calculateRatio());
        this.segments.forEach(seg => seg.getControlPoints().forEach(cp => cp.calculateRatio()));
    }

    buildSegments(coordData) {
        let segments = [];
        let firstCoord = new PolyCoordinate(this.provider, coordData[0][0], coordData[0][1], this, 0);
        let prevCoord = firstCoord;

        for (let i = 1; i < coordData.length; i++) {
            let segment = createSegment(this.provider, coordData[i], this, i - 1, prevCoord);
            segments.push(segment);
            prevCoord = segment.end;
        }

        return segments;
    }

    get coordinates() {
        if (!this.segments) return [];
        let coords = [];
        if (this.segments.length > 0) {
            coords.push(this.segments[0].start);
        }
        for (let seg of this.segments) {
            if (seg.end !== coords[0]) {
                coords.push(seg.end);
            }
        }
        return coords;
    }

    createSupportCoordinates() {
        let [minX, minY, maxX, maxY] = this.getMinMaxCoordinates();

        this.center = new Coordinate(this.provider,
            minX + (maxX - minX) / 2,
            minY + (maxY - minY) / 2
        );

        this.corners = [
            new PolyCornerCoordinate(this.provider, minX, minY, this), // top left
            new PolyCornerCoordinate(this.provider, maxX, maxY, this), // bottom right
            new PolyCornerCoordinate(this.provider, minX, maxY, this), // bottom left
            new PolyCornerCoordinate(this.provider, maxX, minY, this), // top right
        ];

        this.corners[0].setRelations(this.corners[2], this.corners[3], this.corners[1]);
        this.corners[1].setRelations(this.corners[3], this.corners[2], this.corners[0]);
        this.corners[2].setRelations(this.corners[0], this.corners[1], this.corners[3]);
        this.corners[3].setRelations(this.corners[1], this.corners[0], this.corners[2]);

        this.borders = [
            new PolyVerticalBorder(this.provider, this, this.corners[0], this.corners[2]),
            new PolyVerticalBorder(this.provider, this, this.corners[1], this.corners[3]),
            new PolyHorizontalBorder(this.provider, this, this.corners[0], this.corners[3]),
            new PolyHorizontalBorder(this.provider, this, this.corners[1], this.corners[2]),
        ];

        this.createPotentialCoordinates();
    }

    updateSupportCoordinates() {
        let [minX, minY, maxX, maxY] = this.getMinMaxCoordinates();

        this.center.position(
            minX + (maxX - minX) / 2,
            minY + (maxY - minY) / 2
        );

        this.corners[0].position(minX, minY); // top left
        this.corners[1].position(maxX, maxY); // bottom right
        this.corners[2].position(minX, maxY); // bottom left
        this.corners[3].position(maxX, minY); // top right

        this.updatePotentialCoordinates();
    }

    createPotentialCoordinates() {
        this.potentialCoordinates = [];
        for (let i = 0; i < this.segments.length; i++) {
            if (this.segments[i].type === 'straight') {
                this.potentialCoordinates.push(new PolyPotentialCoordinate(this.provider, this, i));
            }
        }
    }

    updatePotentialCoordinates() {
        let straightCount = this.segments.filter(s => s.type === 'straight').length;
        if (!this.potentialCoordinates || this.potentialCoordinates.length !== straightCount) {
            this.createPotentialCoordinates();
            return;
        }
        this.potentialCoordinates.forEach(pc => pc.updatePosition());
    }

    display(g) {
        const opacity = this.p.map(this.currentMaxSize, 500, 1500, 1, 0.25, true);
        g.strokeJoin(g.ROUND);
        g.strokeCap(g.SQUARE);

        this.displayHighlight(g);

        let hasCurves = this.segments.some(s => s.type !== 'straight');
        if (hasCurves || this.close) {
            let ctx = g.drawingContext;
            ctx.save();
            ctx.beginPath();
            this.segments.forEach(seg => seg.displayHighlight(ctx));

            const fillColor = this.appearance.getFillColor(opacity);
            if (fillColor) {
                ctx.fillStyle = fillColor.toString();
                ctx.fill();
            }

            const strokeColor = this.appearance.getStrokeColor(opacity);
            if (strokeColor) {
                ctx.strokeStyle = strokeColor.toString();
                ctx.lineWidth = this.appearance.scaledStrokeWidth;
                ctx.lineJoin = 'round';
                ctx.lineCap = 'square';
                ctx.stroke();
            }
            ctx.restore();
        } else {
            this.appearance.apply(g, opacity);
            g.beginShape();
            this.segments.forEach(seg => seg.display(g));
            g.endShape();
        }

        this.labels.display(g, this.currentMaxSize);

        if (this.editMode && this.selected && this.selection.length == 1) {
            this.borders.map((b) => b.display(g));
            this.potentialCoordinates.map((pc) => pc.display(g));
            this.segments.forEach(seg => seg.getControlPoints().forEach(cp => cp.display(g)));
            this.coordinates.map((c) => c.display(g));
            this.corners.map((c) => c.display(g));
        }

        if (this.selectedSegment) {
            g.stroke(0, 50, 180);
            g.strokeWeight(Math.max(4, this.appearance.scaledStrokeWidth + 1));
            g.strokeCap(g.ROUND);
            this.selectedSegment.displayHover(g);
        }

        if (this.hoveredSegment && this.hoveredSegment !== this.selectedSegment) {
            g.stroke(80, 130, 220);
            g.strokeWeight(Math.max(3, this.appearance.scaledStrokeWidth));
            g.strokeCap(g.ROUND);
            this.hoveredSegment.displayHover(g);
        }
    }

    displayHighlight(g) {
        if (!this.highlight) return;
        this.appearance.drawOuterHighlight(g, this.highlight, (ctx) => {
            this.segments.forEach(seg => seg.displayHighlight(ctx));
        }, 'round');
    }

    mouseOver() {
        // not selected
        if (!this.selected) {
            if (this.hovered()) {
                this.cursor.pointer();
                return true;
            }
            return false;
        }

        // more elements selected
        if (this.selection.length > 1) {
            if (this.hovered()) {
                this.cursor.grab();
                return true;
            }
            return false;
        }

        // single element selected
        if (this.hoveredControlPoints()) {
            this.cursor.move();
            return true;
        }

        if (this.hoveredCoordinates()) {
            this.cursor.move();
            return true;
        }

        if (this.hoveredPotentialCoordinates()) {
            this.cursor.pointer();
            return true;
        }

        if (this.corners.find(c => c.mouseOver())) {
            return true;
        }

        if (this.borders.find(b => b.mouseOver())) {
            return true;
        }

        let hovSeg = this.hoveredSegments();
        if (hovSeg) {
            this.hoveredSegment = hovSeg;
            this.cursor.pointer();
            return true;
        } else {
            this.hoveredSegment = null;
        }

        if (this.labels.mouseOver()) {
            return true;
        }

        if (this.hovered()) {
            this.cursor.grab();
            return true;
        }
        return false;
    }

    deselect() {
        super.deselect();
        this.selectedSegment = null;
        this.hoveredSegment = null;
    }

    move(dx, dy) {
        this.coordinates.map((c) => c.move(dx, dy, false));
        this.segments.forEach(seg => seg.getControlPoints().forEach(cp => cp.move(dx, dy)));
        this.corners.map((c) => c.move(dx, dy, false));
        this.center.move(dx, dy);
        this.potentialCoordinates?.map(c => c.move(dx, dy));
    }

    setPosition(x, y) {
        let dx = x - this.center.absX;
        let dy = y - this.center.absY;
        if (this.settings.snapToGrid) {
            let cx = this.coordinates[0].currentX;
            let cy = this.coordinates[0].currentY;
            let newX = Math.round((Math.round((cx + dx * this.view.scale) / this.grid.minorSize) * this.grid.minorSize) / this.view.scale)
            dx = newX - this.coordinates[0].absX;
            let newY = Math.round((Math.round((cy + dy * this.view.scale) / this.grid.minorSize) * this.grid.minorSize) / this.view.scale)
            dy = newY - this.coordinates[0].absY;
        }
        this.move(dx, dy);
        return [dx, dy];
    }

    hovered() {
        throw new Error('Not implemented');
    }

    hoveredCoordinates() {
        return this.coordinates.find((c) => c.hovered());
    }

    hoveredPotentialCoordinates() {
        return this.potentialCoordinates.find((pc) => pc.hovered());
    }

    hoveredCorners() {
        return this.corners.find((c) => c.hovered());
    }

    hoveredBorders() {
        return this.borders.find((b) => b.hovered());
    }

    hoveredControlPoints() {
        for (let seg of this.segments) {
            for (let cp of seg.getControlPoints()) {
                if (cp.hovered()) return cp;
            }
        }
        return null;
    }

    hoveredSegments() {
        const tolerance = Math.max(8, (this.appearance.scaledStrokeWidth ?? 0) / 2 + 4);
        const mx = this.view.currentX;
        const my = this.view.currentY;
        return this.segments.find(seg => seg.hovered(mx, my, tolerance)) ?? null;
    }

    selectSegment(seg) {
        this.selectedSegment = seg;
    }

    deselectSegment() {
        this.selectedSegment = null;
    }

    hoveredWithSupport() {
        return this.hoveredControlPoints()
            || this.hoveredCoordinates()
            || this.hoveredPotentialCoordinates()
            || this.hoveredCorners()
            || this.hoveredBorders()
            || (this.labels.hovered() ? this.labels : null)
            || (this.hovered() ? this : null)
            || (this.selected && this.selection.length === 1 && this.hoveredSegments() ? this : null);
    }

    surrounded(minX, minY, maxX, maxY) {
        return this.corners[0].absX >= minX
            && this.corners[0].absY >= minY
            && this.corners[1].absX <= maxX
            && this.corners[1].absY <= maxY;
    }

    insertCoordinateOnSegment(segmentIndex, absX, absY) {
        const oldSeg = this.segments[segmentIndex];
        const newCoord = new PolyCoordinate(this.provider, absX, absY, this, 0);
        const seg1 = createStraightSegment(this.provider, oldSeg.start, newCoord, this, 0);
        const seg2 = createStraightSegment(this.provider, newCoord, oldSeg.end, this, 0);
        this.segments.splice(segmentIndex, 1, seg1, seg2);
        this.selectedSegment = null;
        this.hoveredSegment = null;
        this.reindex();
        this.updateSupportCoordinates();
        this.getTab('info').input.multiInput.populate(this);
        return newCoord;
    }

    removeCoordinateAtIndex(coordIndex) {
        let coords = this.coordinates;
        let coord = coords[coordIndex];

        // Find the two segments that share this coordinate
        let segBefore = this.segments.find(s => s.end === coord);
        let segAfter = this.segments.find(s => s.start === coord);
        let beforeIdx = segBefore ? this.segments.indexOf(segBefore) : -1;
        let afterIdx = segAfter ? this.segments.indexOf(segAfter) : -1;

        if (segBefore && segAfter) {
            if (beforeIdx + 1 === afterIdx) {
                // Adjacent segments — simple merge
                let merged = createStraightSegment(this.provider, segBefore.start, segAfter.end, this, 0);
                this.segments.splice(beforeIdx, 2, merged);
            } else {
                // Wrapping case (polygon: closing segment before, first segment after)
                let merged = createStraightSegment(this.provider, segBefore.start, segAfter.end, this, 0);
                // Remove closing segment (last) and first segment, add merged as new closing
                this.segments.splice(afterIdx, 1);
                this.segments.splice(this.segments.length - 1, 1);
                this.segments.push(merged);
            }
        } else if (segAfter && !segBefore) {
            // First coordinate of a line — remove the first segment
            this.segments.splice(0, 1);
        } else if (segBefore && !segAfter) {
            // Last coordinate of a line — remove the last segment
            this.segments.splice(this.segments.length - 1, 1);
        }

        this.selectedSegment = null;
        this.hoveredSegment = null;
        this.reindex();
        this.updateSupportCoordinates();
        this.getTab('info').input.multiInput.populate(this);
    }

    reindex() {
        this.segments.forEach((seg, i) => {
            seg.index = i;
        });
        this.coordinates.forEach((c, i) => {
            c.index = i;
            c.resetInputs();
        });
    }

    segmentLength(index) {
        return this.segments[index].length();
    }

    setSegmentLength(index, length) {
        if (length <= 0) {
            return false;
        }
        let seg = this.segments[index];
        seg.end.setLengthFromCoordinate(length, seg.start);
        this.updateSupportCoordinates();
    }

    hoverSegment(index) {
        this.hoveredSegment = index !== null ? this.segments[index] : null;
    }

    getSegmentCoordinates(index) {
        return [this.segments[index].start, this.segments[index].end];
    }

    exportCoordinates(dx = 0, dy = 0) {
        let coords = [this.segments[0].exportStart(dx, dy)];
        for (let seg of this.segments) {
            // Skip polygon closing segment end (it's the first start)
            if (seg.end === this.segments[0].start) {
                // But export closing segment type/control points if not straight
                if (seg.type !== 'straight') {
                    coords.push(seg.exportClosing());
                }
            } else {
                coords.push(seg.export(dx, dy));
            }
        }
        return coords;
    }

    changeSegmentType(segmentIndex, newType) {
        let seg = this.segments[segmentIndex];
        let newSeg;
        if (newType === 'straight') {
            newSeg = createStraightSegment(this.provider, seg.start, seg.end, this, segmentIndex);
        } else if (newType === 'curve') {
            let cp = CurveSegment.defaultControlPoint(seg.start, seg.end);
            newSeg = createCurveSegment(this.provider, seg.start, seg.end, this, segmentIndex, cp.x, cp.y);
        } else if (newType === 'bezier') {
            let cps = BezierSegment.defaultControlPoints(seg.start, seg.end);
            newSeg = createBezierSegment(this.provider, seg.start, seg.end, this, segmentIndex, cps.cp1.x, cps.cp1.y, cps.cp2.x, cps.cp2.y);
        }
        this.segments[segmentIndex] = newSeg;
        if (this.selectedSegment?.index === segmentIndex) this.selectedSegment = newSeg;
        this.updateSupportCoordinates();
    }

    restoreSegment(segmentIndex, segment) {
        this.segments[segmentIndex] = segment;
        if (this.selectedSegment?.index === segmentIndex) this.selectedSegment = segment;
        this.updateSupportCoordinates();
    }

    export() {
        throw new Error('Not implemented');
    }


    current(value) {
        return value * this.view.scale;
    }

    getMiniView(max) {
        let maxSize = Math.max(Math.abs(this.absWidth), Math.abs(this.absHeight));
        let absMaxSize = this.absMaxSize;
        let newMax = max * (maxSize/absMaxSize);
        let dx = this.center.absX - maxSize / 2;
        let dy = this.center.absY - maxSize / 2;

        let hasCurves = this.segments.some(s => s.type !== 'straight');
        if (!hasCurves) {
            return this.coordinates.map((c) => {
                return [
                    this.p.map(c.absX - dx, 0, maxSize, 0, newMax) + 2 + (max - newMax) / 2,
                    this.p.map(c.absY - dy, 0, maxSize, 0, newMax) + 2 + (max - newMax) / 2,
                ];
            });
        }

        let points = [];
        for (let seg of this.segments) {
            if (seg.end === this.segments[0]?.start) continue; // skip polygon closing
            if (points.length === 0) {
                points.push({ x: seg.start.absX, y: seg.start.absY });
            }
            if (seg.type === 'straight') {
                points.push({ x: seg.end.absX, y: seg.end.absY });
            } else {
                let sampled = seg.samplePoints(8);
                for (let pt of sampled) {
                    points.push(pt);
                }
            }
        }
        return points.map((pt) => {
            return [
                this.p.map(pt.x - dx, 0, maxSize, 0, newMax) + 2 + (max - newMax) / 2,
                this.p.map(pt.y - dy, 0, maxSize, 0, newMax) + 2 + (max - newMax) / 2,
            ];
        });
    }

    getMinMaxCoordinates() {
        let minX = null, minY = null, maxX = null, maxY = null;
        const expand = (x, y) => {
            minX = minX == null ? x : Math.min(minX, x);
            minY = minY == null ? y : Math.min(minY, y);
            maxX = maxX == null ? x : Math.max(maxX, x);
            maxY = maxY == null ? y : Math.max(maxY, y);
        };
        for (let coord of this.coordinates) {
            expand(coord.absX, coord.absY);
        }
        for (let seg of this.segments) {
            if (seg.type !== 'straight') {
                for (let pt of seg.samplePoints(10)) {
                    expand(pt.x, pt.y);
                }
            }
        }
        return [minX, minY, maxX, maxY];
    }

    getClosestCoordinate() {
        let coordinate = this.coordinates[0];
        let distance = this.view.coordinate.absDistanceTo(coordinate);

        this.coordinates.map((c, index) => {
            if (index == 0) {
                return;
            }
            let d = this.view.coordinate.absDistanceTo(c);
            if (d < distance) {
                distance = d;
                coordinate = c;
            }
        });

        return coordinate;
    }

    get currentWidth() {
        return this.corners[1].currentX - this.corners[0].currentX;
    }

    get currentHeight() {
        return this.corners[1].currentY - this.corners[0].currentY;
    }

    get absWidth() {
        return this.corners[1].absX - this.corners[0].absX;
    }

    get absHeight() {
        return this.corners[1].absY - this.corners[0].absY;
    }

    get currentMaxSize() {
        return this.absMaxSize * this.view.scale;
    }

    get absMaxSize() {
        return Math.max(Math.abs(this.absWidth), Math.abs(this.absHeight)) + (this.appearance.strokeWidth ?? 0);
    }

    get currentMinSize() {
        return this.absMinSize * this.view.scale;
    }

    get absMinSize() {
        return Math.min(Math.abs(this.absWidth), Math.abs(this.absHeight));
    }

    get absMinX() {
        return this.corners[0].absX;
    }

    get absMaxX() {
        return this.corners[1].absX;
    }

    get absMinY() {
        return this.corners[0].absY;
    }

    get absMaxY() {
        return this.corners[1].absY;
    }

    get length() {
        throw new Error('Not implemented');
    }
}
